/**
 * Rejecting a recommendation or closing a finding: a reason a reader can act on
 * (five characters is the floor below which none is), and optionally a file
 * in support — the email, the exemption, the screenshot. A dialog, so the
 * decision is made deliberately and the reason is written where it is read.
 */
import {
  DISMISS_CATEGORIES,
  DISMISS_CATEGORY_LABELS,
  type DismissCategory,
} from "@repo/shared/findingPlanning";
import { PaperclipIcon } from "lucide-react";
import { useRef, useState } from "react";

import type { DecisionProofUpload } from "../../../state/governance";
import { fileToBase64, formatBytes } from "../../../state/policies";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { Radio, RadioGroup } from "../../ui/radio-group";
import { Textarea } from "../../ui/textarea";

export const MIN_REASON_LENGTH = 5;

/** What each dismissal category asks the note to say. */
export const DISMISS_CATEGORY_HINTS: Readonly<Record<DismissCategory, string>> = {
  not_applicable: "The requirement does not apply to this business or document.",
  already_covered: "Another section or document already meets it. Say where.",
  risk_accepted: "The gap is known and accepted. Record who accepted it.",
  duplicate: "Another finding already tracks this gap.",
};
const MAX_PROOF_BYTES = 10 * 1024 * 1024;

export function RejectDialog({
  open,
  onOpenChange,
  what,
  title,
  onReject,
  allowProof = true,
  dismiss = false,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** "recommendation" or "finding" — what the reason is about. */
  readonly what: string;
  /** The item's own title, so the dialog says what it is rejecting. */
  readonly title: string | null;
  readonly onReject: (input: {
    readonly reason: string;
    readonly proof?: DecisionProofUpload;
    /** Set when the dialog asks for a category (`dismiss`). */
    readonly category?: DismissCategory;
  }) => Promise<string | null>;
  /** Offer a supporting file. Off where the decision cannot keep one. */
  readonly allowProof?: boolean;
  /**
   * Dismiss a finding: a reason category is required beside the note, and
   * the dialog says "Dismiss" rather than "Close" or "Reject".
   */
  readonly dismiss?: boolean;
}) {
  const closing = what === "finding";
  const inputRef = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState<DismissCategory | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();
  const short = trimmed.length < MIN_REASON_LENGTH;
  const missing = MIN_REASON_LENGTH - trimmed.length;

  const reset = () => {
    setReason("");
    setCategory(null);
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    if (submitting.current) return;
    if (dismiss && category === null) {
      setError("Choose why it is dismissed.");
      return;
    }
    if (short) {
      setError(`Give a reason of at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }
    if (file !== null && file.size > MAX_PROOF_BYTES) {
      setError("The supporting file is over 10 MB.");
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const proof = file === null || !allowProof ? undefined
        : { filename: file.name, contentBase64: await fileToBase64(file) };
      const message = await onReject({
        reason: trimmed,
        ...(proof === undefined ? {} : { proof }),
        ...(dismiss && category !== null ? { category } : {}),
      });
      if (message !== null) {
        setError(message);
        return;
      }
      reset();
      onOpenChange(false);
    } catch {
      setError(`The ${closing ? "closure" : "rejection"} could not be saved. Your reason is kept here; try again.`);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting.current) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dismiss ? "Dismiss finding" : closing ? "Close finding" : `Reject this ${what}`}</DialogTitle>
          <DialogDescription>
            {title ? `"${title}" — ` : ""}
            say why it does not apply. The reason is recorded with your name; a supporting file
            is optional.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4" aria-busy={busy}>
          <fieldset disabled={busy} className="min-w-0 space-y-4">
          {dismiss ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Reason</p>
              <RadioGroup
                value={category ?? ""}
                onValueChange={(value) => {
                  setCategory(value as DismissCategory);
                  setError(null);
                }}
                className="grid gap-1.5 sm:grid-cols-2"
                aria-label="Reason category"
              >
                {DISMISS_CATEGORIES.map((option) => (
                  <label
                    key={option}
                    className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border/70 px-3 py-2.5 transition-colors hover:bg-muted/40 has-data-checked:border-primary/40 has-data-checked:bg-primary/[0.04]"
                  >
                    <Radio value={option} className="mt-0.5" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{DISMISS_CATEGORY_LABELS[option]}</span>
                      <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{DISMISS_CATEGORY_HINTS[option]}</span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          ) : null}
          <label className="block space-y-1.5">
            <span className="flex items-center justify-between text-xs font-medium text-muted-foreground">
              <span>{dismiss ? "Note" : "Reason"}</span>
              <span className={short ? "text-muted-foreground" : "text-success"}>
                {short ? `${missing} more ${missing === 1 ? "character" : "characters"}` : "Good"}
              </span>
            </span>
            <Textarea
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setError(null);
              }}
              placeholder="e.g. Already covered by the Group AML Framework, section 4.2"
              rows={3}
              className="resize-none"
              autoFocus
            />
          </label>
          {allowProof ? (
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-muted/20 px-4 py-3">
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setError(null);
              }}
            />
            <PaperclipIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              {file === null ? (
                <>
                  <span className="block text-sm font-medium text-foreground">Supporting proof</span>
                  <span className="block text-xs text-muted-foreground">
                    Optional — an email, an exemption letter, a screenshot
                  </span>
                </>
              ) : (
                <>
                  <span className="block truncate text-sm font-medium text-foreground">{file.name}</span>
                  <span className="block text-xs text-muted-foreground">{formatBytes(file.size)}</span>
                </>
              )}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              className="shrink-0 cursor-pointer"
            >
              {file === null ? "Attach file" : "Change"}
            </Button>
          </div>
          ) : null}
          </fieldset>
          {error !== null && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </DialogPanel>
        <DialogFooter>
          <DialogClose
            render={
              <Button type="button" size="sm" variant="ghost" disabled={busy} className="cursor-pointer">
                Cancel
              </Button>
            }
          />
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => void submit()}
            disabled={busy || short || (dismiss && category === null)}
            className="cursor-pointer"
          >
            {busy
              ? dismiss
                ? "Dismissing…"
                : closing
                  ? "Closing…"
                  : "Recording rejection…"
              : dismiss
                ? "Dismiss finding"
                : closing
                  ? "Close finding"
                  : "Reject"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
